import { Box, Space, SpaceVertical, TextArea } from '@looker/components';
import { BIIconButton, globalTheme } from '@pulsepointinc/bi-components';
import { DsCheckbox, DsRadio, DsRadioGroup, DsTypography } from '@pulsepointinc/design-system-react';
import type React from 'react';
import { type FC, useId } from 'react';
import type { Filter } from '../../../../../../../stores/filters';
import { usePageURLFilter } from './hooks/usePageURLFilter';
import { AppliedText, EditButton } from './styles';

interface PageURLFilterProps {
  onChange: (filter: Filter) => void;
  filterName?: string;
  isHCPSite?: boolean;
  label?: string;
  placeholder?: string;
  radioLabelSuffix?: string;
}

const DEFAULT_FILTER_NAME = 'site_page_url_filter';

const PageURLFilter: FC<PageURLFilterProps> = ({
  // Any suggestions on renaming this component?
  onChange,
  filterName,
  isHCPSite = false,
  label = 'Page URL Filter',
  placeholder = 'Add one URL per line',
  radioLabelSuffix = 'URLs',
}) => {
  const frequencyCheckboxId = useId();
  const radioGroupId = useId();
  const resolvedFilterName = filterName ?? DEFAULT_FILTER_NAME;

  const {
    inputValue,
    isPageURLControlEnabled,
    expressionType,
    isEditMode,
    appliedItems,
    handleInputChange,
    handleCheckboxChange,
    handleRadioChange,
    handleApply,
    handleEdit,
  } = usePageURLFilter({ filterName: resolvedFilterName, onChange, isHCPSite });

  return (
    <SpaceVertical gap="u2">
      <Space gap="u2" align="center">
        <DsCheckbox
          id={frequencyCheckboxId}
          onCheckedChange={(event: CustomEvent<boolean>) => handleCheckboxChange(event.detail)}
          checked={isPageURLControlEnabled}
        >
          <DsTypography as="span" variant={isHCPSite ? 'body' : 'label'}>
            {isHCPSite ? `Enable ${label}` : label}
          </DsTypography>
        </DsCheckbox>
      </Space>

      {isHCPSite && isPageURLControlEnabled && !isEditMode && appliedItems.length > 0 && (
        <SpaceVertical gap="u1" paddingLeft="u8">
          <AppliedText color="secondary">{appliedItems.join(', ')}</AppliedText>
          <EditButton color="invisible" onClick={handleEdit}>
            Edit {radioLabelSuffix}
          </EditButton>
        </SpaceVertical>
      )}

      {isPageURLControlEnabled && (isEditMode || !isHCPSite) && (
        <Box
          width="100%"
          padding={isHCPSite ? 'u4' : undefined}
          paddingLeft={isHCPSite ? undefined : 'u8'}
          backgroundColor={isHCPSite ? globalTheme.colors.backgroundLight : undefined}
          borderRadius={isHCPSite ? '6px' : undefined}
        >
          <DsRadioGroup
            name={radioGroupId}
            orientation="horizontal"
            value={expressionType}
            onValueChange={(e) => handleRadioChange(e.detail as 'target' | 'block')}
          >
            <DsRadio name={radioGroupId} label={`Include ${radioLabelSuffix}`} value="target" />
            <DsRadio name={radioGroupId} label={`Exclude ${radioLabelSuffix}`} value="block" />
          </DsRadioGroup>

          <TextArea
            value={inputValue as string}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
              handleInputChange(e.target.value);
            }}
            placeholder={placeholder}
            resize="none"
            minHeight="200px"
            maxHeight="calc(100vh - 40px)"
            marginTop="u2"
            width="100%"
          />

          {isHCPSite && (
            <Space gap="u3" marginTop="u4" width="100%" color={globalTheme.colors.textPrimary} justify="end">
              <BIIconButton
                icon="Clear"
                label="Clear"
                onClick={() => {
                  handleInputChange('');
                  handleCheckboxChange(false);
                }}
              />
              <BIIconButton icon="CheckCircle" label="Apply" disabled={!inputValue} onClick={handleApply} />
            </Space>
          )}
        </Box>
      )}
    </SpaceVertical>
  );
};

export default PageURLFilter;
